import config from 'app/config/config'
import { client } from 'app/shared/api/client'
import { Dispatch } from 'redux'
import {
  addLoan,
  editLoan,
  submitLoan,
  approveLoan,
  rejectLoan,
  getApprovedLoans,
  getAllApprovedLoans,
  deleteLoan,
  getLoanHistory,
  getLoan,
  getFacilityLoans,
  getAllLoans,
  getLockedLoansByFacility,
  startLoadingFacilityLoans,
  stopLoadingFacilityLoans,
  getLoanAmounts,
} from 'app/state/ducks/loans/actions'
import { DraftLoanParams, LoanParams } from 'app/models/loan-params'
import {
  coalesceThunk,
  coalesceThunkByKey,
  coalescePromiseByKey,
} from 'app/state/utils/coalesce-thunk'

export const saveLoan =
  (savedLoan: DraftLoanParams, loan?: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const updated_savedLoan: DraftLoanParams = {
      ...savedLoan,
    }
    if (loan) {
      return client
        .put(`${apiServerUrl}/api/draft-loans`, updated_savedLoan)
        .then(response => {
          const payload = dispatch(editLoan(response))
          return { success: true, payload: payload.payload }
        })
        .catch(e => {
          return { success: false, payload: e.response.data.error }
        })
    }

    return client
      .post(`${apiServerUrl}/api/draft-loans`, updated_savedLoan)
      .then(response => {
        const payload = dispatch(addLoan(response))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadOneLoan = coalesceThunkByKey(
  (loanId: string, status: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    const statusLower = status.toLowerCase()
    return client
      .get(`${apiServerUrl}/api/${statusLower}-loans/one?id=${loanId}`)
      .then(loan => {
        if (statusLower === 'submitted') {
          return dispatch(getLoan({ ...loan.data.loan, status: status }))
        }
        if (statusLower === 'locked') {
          return dispatch(getLoan({ ...loan.data.loan, status: status }))
        }
        return dispatch(getLoan({ ...loan.data, status: status }))
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const loadLoanId = coalesceThunkByKey(
  (loanId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-loans/one?id=${loanId}`)
      .then(response => {
        return dispatch(getApprovedLoans(response.data))
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  },
)

export const loadLoans = coalesceThunkByKey(
  (facilityId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(
        `${apiServerUrl}/api/approved-loans/facility?id=${facilityId}`,
      )
      .then(response => {
        return dispatch(getApprovedLoans(response.data))
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const loadApprovedLoans = coalescePromiseByKey(
  async (facilityId?: string) => {
    const apiServerUrl = config.backendUrl
    return await client
      .get(
        `${apiServerUrl}/api/approved-loans${
          facilityId ? `/facility?id=${facilityId}` : ''
        }`,
      )
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const loadAllApprovedLoans = coalesceThunk(
  () => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return await client
      .get(`${apiServerUrl}/api/approved-loans`)
      .then(response => {
        return dispatch(getAllApprovedLoans(response.data))
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const loadDraftLoans = coalescePromiseByKey(
  async (facilityId?: string) => {
    const apiServerUrl = config.backendUrl
    return await client
      .get(
        `${apiServerUrl}/api/draft-loans${
          facilityId ? `/facility?id=${facilityId}` : ''
        }`,
      )
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const loadSubmittedLoans = coalescePromiseByKey(
  async (facilityId?: string) => {
    const apiServerUrl = config.backendUrl
    return await client
      .get(
        `${apiServerUrl}/api/submitted-loans${
          facilityId ? `/facility?id=${facilityId}` : ''
        }`,
      )
      .then(response => {
        return response.data
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const loadLockedLoans = coalesceThunkByKey(
  (facilityId?: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return await client
      .get(
        `${apiServerUrl}/api/locked-loans${
          facilityId ? `/facility?id=${facilityId}` : ''
        }`,
      )
      .then(response => {
        const reshapedData = response.data.map((item: any) => ({
          ...item.loan,
          status: 'Locked',
        }))
        dispatch(getLockedLoansByFacility(reshapedData))
      })
      .catch(e => {
        console.log(e)
        return []
      })
  },
)

export const submitForApproval = (loan: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/draft-loans/submit`, { id: loan.id })
    .then(response => {
      const payload = dispatch(submitLoan(response.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const approveSelectedLoan =
  (loan: any) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .post(`${apiServerUrl}/api/submitted-loans/approve`, { id: loan.id })
      .then(approveResponse => {
        const loanData = (approveResponse.data as any).extraData
        const payload = dispatch(approveLoan(loanData))
        return { success: true, payload: payload.payload }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const rejectSelectedLoan = (loan: any) => async (dispatch: Dispatch) => {
  const apiServerUrl = config.backendUrl
  return client
    .post(`${apiServerUrl}/api/submitted-loans/reject`, { id: loan.id })
    .then(response => {
      const payload = dispatch(rejectLoan(response.data))
      return { success: true, payload: payload.payload }
    })
    .catch(e => {
      return { success: false, payload: e.response.data.error }
    })
}

export const deleteSelectedLoan =
  (loan: LoanParams) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    let loanDeleteUrl
    if (loan.status === 'Approved') {
      loanDeleteUrl = `${apiServerUrl}/api/approved-loans?ids[]=${loan.id}`
    } else {
      loanDeleteUrl = `${apiServerUrl}/api/draft-loans?ids[]=${loan.id}`
    }
    return client
      .delete(loanDeleteUrl)
      .then(loan => {
        //we can do this because currently we only handle deleting one loan at a time
        if (loan.data[0].error) {
          return { success: false, payload: loan.data[0].error }
        } else {
          dispatch(deleteLoan(loan.data))
          return { success: true, payload: loan.data }
        }
      })
      .catch(e => {
        return { success: false, payload: e.response.data.error }
      })
  }

export const loadAllLoans = coalesceThunkByKey(
  (facilityId?: string) => async (dispatch: Dispatch) => {
    if (facilityId) {
      dispatch(startLoadingFacilityLoans())
    }

    try {
      let all_loans: LoanParams[] = []

      const [draftLoans, submittedLoans, loans] = await Promise.all([
        loadDraftLoans(facilityId),
        loadSubmittedLoans(facilityId),
        loadApprovedLoans(facilityId),
      ])

      const arr = submittedLoans.map((a: { loan: any }) => a.loan)
      const sub_loans = arr.map((a: any) => ({ ...a, status: 'Submitted' }))

      all_loans = draftLoans
        .map((loan: any) => ({ ...loan, status: 'Draft' }))
        .concat(sub_loans)
        .concat(loans.map((loan: any) => ({ ...loan, status: 'Approved' })))

      if (facilityId) {
        return dispatch(getFacilityLoans(all_loans))
      } else {
        return dispatch(getAllLoans(all_loans))
      }
    } catch (error) {
      console.log('THUNK ERROR: ', error)
    } finally {
      if (facilityId) {
        dispatch(stopLoadingFacilityLoans())
      }
    }
  },
)

export const loadLoanHistory = coalesceThunkByKey(
  (loanId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-loans/history?id=${loanId}`)
      .then(response => {
        return dispatch(getLoanHistory(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const loadLoanAmounts = coalesceThunkByKey(
  (loanId: string) => async (dispatch: Dispatch) => {
    const apiServerUrl = config.backendUrl
    return client
      .get(`${apiServerUrl}/api/approved-loans/amounts?id=${loanId}`)
      .then(response => {
        return dispatch(getLoanAmounts(response.data))
      })
      .catch(error => {
        console.log('THUNK ERROR: ', error)
      })
  },
)

export const fetchLoanHistoryDirect = async (loanId: string) => {
  const apiServerUrl = config.backendUrl
  try {
    const response = await client.get(
      `${apiServerUrl}/api/approved-loans/history?id=${loanId}`,
    )
    return response.data
  } catch (error) {
    console.log('Error fetching loan history:', error)
    return null
  }
}

export const fetchLoanAmountsDirect = async (loanId: string) => {
  const apiServerUrl = config.backendUrl
  try {
    const response = await client.get(
      `${apiServerUrl}/api/approved-loans/amounts?id=${loanId}`,
    )
    return response.data
  } catch (error) {
    console.log('Error fetching loan amounts:', error)
    return null
  }
}
